"use client";
import React from "react";
import Icon from "@/components/icons";
import { IconTypes } from "@/components/icons/types";

interface OptionButtonProps {
  optionValue: string;
  optionLabel: string;
  isSelected: boolean;
  isBasic: boolean;
  optionStyling: string;
  icon?: string;
  description?: string;
  onToggle: (value: string, label: string) => void;
  selectionType?: "single" | "multiple";
  groupName?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLElement>) => void;
  index?: number;
  isTabbable?: boolean;
  setRef?: (element: HTMLElement | null) => void;
}

export const OptionButton: React.FC<OptionButtonProps> = ({
  optionValue,
  optionLabel,
  isSelected,
  isBasic,
  optionStyling,
  icon,
  description,
  onToggle,
  selectionType = "multiple",
  onKeyDown,
  index = 0,
  isTabbable = true,
  setRef,
}) => {
  const contentComponent = (
    <>
      {!isBasic && icon && (
        <div className="flex justify-center items-center p-1">
          <Icon
            type={icon as IconTypes}
            svgProps={{ width: "20", height: "20" }}
          />
        </div>
      )}
      {isBasic ? (
        <div className="font-bold text-base">{optionLabel}</div>
      ) : (
        <span className="font-bold text-lg text-brand-500">{optionLabel}</span>
      )}
      {description && (
        <>
          {isBasic ? (
            <div className="text-sm opacity-80">{description}</div>
          ) : (
            <span className="text-base opacity-80 text-gray-900">
              {description}
            </span>
          )}
        </>
      )}
    </>
  );

  if (selectionType === "single") {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Only select on Enter or Space, not on arrow keys
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle(optionValue, optionLabel);
      }
      // Let parent handle arrow key navigation
      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    const handleClick = () => {
      onToggle(optionValue, optionLabel);
    };

    // Use roving tabindex pattern for radio group keyboard navigation
    const tabIndex = isTabbable ? 0 : -1;

    return (
      <div
        role="radio"
        aria-checked={isSelected}
        tabIndex={tabIndex}
        ref={setRef as React.Ref<HTMLDivElement> | undefined}
        className={`${optionStyling}`}
        onKeyDown={handleKeyDown}
        onClick={handleClick}
        data-index={index}
      >
        {contentComponent}
      </div>
    );
  }

  return (
    <button
      onClick={() => onToggle(optionValue, optionLabel)}
      onKeyDown={
        onKeyDown as React.KeyboardEventHandler<HTMLButtonElement> | undefined
      }
      ref={setRef as React.Ref<HTMLButtonElement> | undefined}
      className={optionStyling}
      aria-pressed={isSelected}
    >
      {contentComponent}
    </button>
  );
};
